import { useState, useEffect } from 'react';
import axios from 'axios';

const PreferenceSelector = () => {
  const [mode, setMode] = useState('cohort');
  const isCohort = mode === 'cohort';
  const label = isCohort ? 'Cohort' : 'Prompt';


  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState('all');
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchOrgData = async () => {
      if (!selectedOrgId) return;

      try {
        const preferencesResponse = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/getFeedPreferences', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        const orgPreferences = preferencesResponse.data.find(
          (p) => p.organization_id === parseInt(selectedOrgId)
        );

        if (orgPreferences) {
          setSelectedItems(orgPreferences[isCohort ? 'cohorts' : 'prompts'] || []);
          setSelectedTimeline(orgPreferences.timeline || 'all');
          setPostCount(orgPreferences.preference_type === mode ? orgPreferences.post_count || 0 : 0);
        }

        const endpoint = isCohort ? 'cohorts' : 'prompts';
        const response = await axios.get(`https://genz-staging.feeltiptop.com/api/social_glass/${endpoint}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { organization_id: selectedOrgId },
        });

        setItems(response.data);
      } catch (error) {
        console.error(`Error fetching ${label.toLowerCase()}s or preferences:`, error);
      }
    };

    fetchOrgData();
  }, [selectedOrgId, isCohort, label, mode]);

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setSelectedItems([]);
    setPostCount(0);
    setStatusMsg('');
  };

  const handleSelection = (e) => {
    const { value, checked } = e.target;
    const id = parseInt(value);
    setSelectedItems((prev) =>
      checked ? [...prev, id] : prev.filter((itemId) => itemId !== id)
    );
  };

  const fetchPostCount = async () => {
    if (selectedItems.length === 0) return;

    try {
      const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/posts/count', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: {
          [`${isCohort ? 'cohort_ids' : 'prompt_ids'}`]: selectedItems.join(','),
          timeline: selectedTimeline,
        },
      });

      setPostCount(response.data.totalPosts);
    } catch (error) {
      console.error('Error fetching post count:', error);
    }
  };

  const savePreferences = async () => {
    if (selectedItems.length === 0) return;

    try {
      await axios.post(
        'https://genz-staging.feeltiptop.com/api/social_glass/saveFeedPreferences',
        {
          organization_id: selectedOrgId,
          [`${isCohort ? 'cohort_ids' : 'prompt_ids'}`]: selectedItems,
          timeline: selectedTimeline,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );

      setStatusMsg('✅ Preferences saved successfully.');
    } catch (error) {
      console.error('Error saving preferences:', error);
      setStatusMsg('❌ Error saving preferences.');
    }
  };

  const handleUpdatePreferences = async () => {
    await savePreferences();
    await fetchPostCount();
  };

  return (
    <div className="min-h-screen max-w-5xl mx-auto p-6 bg-white rounded-xl shadow space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Customize Feed by {label}</h1>

        <div className="flex items-center space-x-3">
          <span className={`text-sm font-medium ${!isCohort ? 'text-gray-900' : 'text-gray-400'}`}>Prompt</span>

          <label className="relative inline-flex items-center cursor-pointer w-11 h-6">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isCohort}
              onChange={() => setMode(isCohort ? 'prompt' : 'cohort')}
            />
            <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-indigo-600 transition-colors duration-300"></div>
            <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 peer-checked:translate-x-5" />
          </label>

          <span className={`text-sm font-medium ${isCohort ? 'text-gray-900' : 'text-gray-400'}`}>Cohort</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700">Select Organization:</label>
        <select
          value={selectedOrgId}
          onChange={handleOrgChange}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        >
          <option value="">Select an Organization</option>
          {organizations.map((org) => (
            <option key={org.OrganizationID} value={org.OrganizationID}>
              {org.Name}
            </option>
          ))}
        </select>
      </div>

      {selectedOrgId && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-700">Select {label}s:</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.length > 0 ? (
              items.map((item) => (
                <label key={item[`${label}ID`]} className="flex items-start sm:items-center space-x-2">
                  <input
                    type="checkbox"
                    value={item[`${label}ID`]}
                    checked={selectedItems.includes(item[`${label}ID`])}
                    onChange={handleSelection}
                    className="mt-1 sm:mt-0"
                  />
                  <span className="text-gray-700 break-words max-w-full">
                    {isCohort ? item.Name : item.PromptText}
                  </span>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-500">No {label.toLowerCase()}s found for this organization.</p>
            )}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700">Select Timeline:</label>
        <select
          value={selectedTimeline}
          onChange={(e) => setSelectedTimeline(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        >
          <option value="day">Last Day</option>
          <option value="week">Last 7 Days</option>
          <option value="2weeks">Last 14 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      <div className="text-gray-700 font-medium">
        Total Posts for Selected {label}s: <span className="font-bold">{postCount}</span>
      </div>

      <button
        onClick={handleUpdatePreferences}
        className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition"
      >
        Update Preferences
      </button>

      {statusMsg && (
        <div
          className={`text-sm p-2 rounded ${
            statusMsg.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {statusMsg}
        </div>
      )}
    </div>
  );
};

export default PreferenceSelector;
